import React from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Quote, ArrowRight } from 'lucide-react';

const Section = styled.section`
  padding: 100px 24px;
  background-color: #002147;
  color: white;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSide = styled(motion.div)`
  z-index: 2;
`;

const Label = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.2;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 40px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: #4ecdc4;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const QuoteCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-left: 4px solid #4ecdc4;
  padding: 24px;
  border-radius: 0 12px 12px 0;
  font-style: italic;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.6;
`;

const ReadStoryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #4ecdc4;
  color: #002147;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 700;
  text-decoration: none;
  margin-top: 32px;
  transition: all 0.2s ease;

  &:hover {
    background: #3db9b0;
    transform: translateY(-2px);
  }
`;

const VisualSide = styled(motion.div)`
  position: relative;
  height: 500px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  background: #1a1a1a; /* Fallback */

  @media (max-width: 960px) {
    height: 400px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%);
  z-index: 1;
`;

const FloatingBadge = styled(motion.div)`
  position: absolute;
  bottom: 32px;
  left: 32px;
  background: white;
  padding: 16px 24px;
  border-radius: 16px;
  color: #002147;
  z-index: 2;
  box-shadow: 0 12px 24px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EducationCaseStudy: React.FC = () => {
  return (
    <Section>
      <Container>
        <ContentSide
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Label>
            <TrendingUp size={16} /> Case Study
          </Label>
          <Title>
            How Premier TEFL Automated 70% of Student Support
          </Title>
          <Description>
            Facing a surge in international enrollments, Premier TEFL needed a way to support 7,000+ students across different time zones without burning out their team.
          </Description>

          <StatsGrid>
            <StatItem>
              <StatValue>70%</StatValue>
              <StatLabel>Routine Issues Automated</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>45%</StatValue>
              <StatLabel>Reduction in Query Time</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>24/7</StatValue>
              <StatLabel>Student Availability</StatLabel>
            </StatItem>
          </StatsGrid>

          <QuoteCard>
            <Quote size={20} style={{ marginBottom: '12px', opacity: 0.5 }} />
            "Luna (our AI agent) has been a game-changer. It handles the repetitive 'is this accredited?' questions instantly, letting our team focus on complex student needs."
          </QuoteCard>

          <ReadStoryButton to="/blog/premier-tefl-case-study">
            Read our story <ArrowRight size={18} />
          </ReadStoryButton>
        </ContentSide>

        <VisualSide
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Placeholder for a real image - using a high quality Unsplash education image for now */}
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80"
            alt="Students collaborating"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <ImageOverlay />

          <FloatingBadge
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div style={{ background: '#e0f2f1', padding: '8px', borderRadius: '50%', color: '#005f73' }}>
              <Users size={20} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Students Supported</div>
              <div style={{ fontSize: '18px', fontWeight: 800 }}>7,000+</div>
            </div>
          </FloatingBadge>
        </VisualSide>
      </Container>
    </Section>
  );
};

export default EducationCaseStudy;
